import { useEffect, useState } from 'react'
import { prettyExpr } from '../game24/format'

// Fallback answer entry, shown UNDER the transcript when speech detection didn't
// confidently hear a solution. A constrained math keyboard — the four puzzle numbers
// as keys, plus operators and parentheses — so the typed answer scores exactly.
export const AnswerKeypad = ({
  numbers,
  onSubmit,
  onPass,
}: {
  numbers: number[]
  onSubmit: (expr: string) => void
  onPass?: () => void
}) => {
  const [expr, setExpr] = useState('')
  const add = (token: string) => setExpr((e) => e + token)
  const backspace = () => setExpr((e) => e.slice(0, -1))
  const clear = () => setExpr('')

  // Physical keyboard support (desktop): map digits, operators (incl. x for ×),
  // parentheses, Backspace, Enter (submit) and Escape (clear). The on-screen keypad
  // and the physical keyboard drive the same expression.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key
      if (k === 'Enter') {
        if (expr.trim()) onSubmit(expr)
        e.preventDefault()
        return
      }
      if (k === 'Backspace') {
        backspace()
        e.preventDefault()
        return
      }
      if (k === 'Escape') {
        clear()
        return
      }
      let token = ''
      if (/^[0-9]$/.test(k)) token = k
      else if (k === '+' || k === '-' || k === '(' || k === ')') token = k
      else if (k === '*' || k === 'x' || k === 'X' || k === '×') token = '*'
      else if (k === '/' || k === '÷') token = '/'
      else return // ignore anything else
      add(token)
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expr, onSubmit])

  const opKey = (label: string, token: string) => (
    <button
      key={token}
      type="button"
      onClick={() => add(token)}
      className="rounded-md bg-gray-100 py-1.5 text-base font-semibold text-gray-700 hover:bg-gray-200"
    >
      {label}
    </button>
  )

  return (
    <div className="text-center">
      <p className="text-sm font-medium text-gray-700">
        To verify your answer, please re-enter it using the keyboard below.
      </p>
      <p className="mt-0.5 text-xs text-gray-400">
        Or if you didn’t crack it, that’s fine — tell us below.
      </p>

      {/* Compact entry: small display + keys */}
      <div className="mt-2 min-h-[2.25rem] rounded-md border border-gray-300 px-2 py-1.5 text-lg text-gray-800 flex items-center justify-center">
        {expr ? (
          <span>
            {prettyExpr(expr)}
            <span className="ml-0.5 animate-pulse text-gray-400">▏</span>
          </span>
        ) : (
          <span className="text-gray-300 text-sm">type or tap your answer</span>
        )}
      </div>

      <div className="mt-2 grid grid-cols-4 gap-1.5">
        {numbers.map((n, i) => (
          <button
            key={i}
            type="button"
            onClick={() => add(String(n))}
            className="rounded-md bg-[#820458] py-1.5 text-base font-bold text-white hover:opacity-90"
          >
            {n}
          </button>
        ))}
      </div>

      <div className="mt-1.5 grid grid-cols-6 gap-1.5">
        {opKey('+', '+')}
        {opKey('−', '-')}
        {opKey('×', '*')}
        {opKey('÷', '/')}
        {opKey('(', '(')}
        {opKey(')', ')')}
      </div>

      <div className="mt-1.5 grid grid-cols-3 gap-1.5">
        <button
          type="button"
          onClick={backspace}
          className="rounded-md bg-gray-100 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-200"
        >
          ⌫ Delete
        </button>
        <button
          type="button"
          onClick={clear}
          className="rounded-md bg-gray-100 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-200"
        >
          Clear
        </button>
        <button
          type="button"
          disabled={expr.trim().length === 0}
          onClick={() => onSubmit(expr)}
          className="rounded-md bg-[#398874] py-1.5 text-xs font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Submit
        </button>
      </div>

      {/* Give up — the player committed but has no correct answer to enter. No keep-going
          nudge here: solving time ended at Commit (unlike the PASS during play). */}
      {onPass && (
        <button
          type="button"
          onClick={onPass}
          className="mt-4 w-full rounded-lg border-2 border-gray-300 py-4 text-base font-bold tracking-wide text-gray-500 hover:bg-gray-50"
        >
          I didn’t solve it
        </button>
      )}
    </div>
  )
}
